import { combineLoaders } from '@/lib/loader/combine-loader';
import { permissionsLoader } from '@/lib/loader/permission-loader';
import { lazy } from 'react';
import { FeatureFlagKey } from '@/features/feature-flag/feature-flag.constants';
import { withFeature } from '@/features/feature-flag/components/with-feature';

const companyDetail = () => import('./pages/general');
const GeneralTab = lazy(() =>
  companyDetail().then(m => ({ default: m.GeneralTab }))
);
const BetaaloverzichtTab = lazy(() =>
  companyDetail().then(m => ({ default: m.BetaaloverzichtTab }))
);
const GiftCardTab = lazy(() =>
  companyDetail().then(m => ({ default: m.GiftCardTab }))
);
// Tax Setting moved to the Superadmin Console — see `consoleRoute`.
const ResellerSettingPage = lazy(() => import('./pages/reseller-setting'));
import ResellerForm from './pages/reseller-setting/form';
const GroupTypeSettingPage = lazy(() => import('./pages/group-type-setting'));
import GroupTypeForm from './pages/group-type-setting/form';
const AgeGroupPresetPage = lazy(() => import('./pages/age-group-preset'));
import AgeGroupPresetForm from './pages/age-group-preset/form';
const GroupSizePresetPage = lazy(() => import('./pages/group-size-preset'));
import GroupSizePresetForm from './pages/group-size-preset/form';
const GiftCardValueSettingPage = lazy(
  () => import('./pages/gift-card-value-setting')
);
import GiftCardValueSettingForm from './pages/gift-card-value-setting/form';

const PromoBannerPage = lazy(() => import('./pages/promo-banner'));
const PaymentOptionsPage = lazy(() => import('./pages/payment-options'));
const GoogleCalendarPage = lazy(() => import('./pages/google-calendar'));
import GoogleCalendarForm from './pages/google-calendar/form';
const GoogleOAuthCallback = lazy(
  () => import('./pages/google-calendar/oauth-callback')
);

export const settingCompanyRoute = [
  // Three tabs of one company-detail record, each a page with its own form.
  {
    path: '/setting/company/general',
    Component: GeneralTab,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/betaaloverzicht',
    Component: withFeature(
      FeatureFlagKey.MODULE_PAYMENT_OVERVIEW,
      BetaaloverzichtTab,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/payment-options',
    Component: PaymentOptionsPage,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/gift-card',
    Component: withFeature(
      FeatureFlagKey.MODULE_GIFT_CARDS,
      GiftCardTab,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/gift-card-values',
    Component: withFeature(
      FeatureFlagKey.MODULE_GIFT_CARDS,
      GiftCardValueSettingPage,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/gift-card-values/form/',
    Component: withFeature(
      FeatureFlagKey.MODULE_GIFT_CARDS,
      GiftCardValueSettingForm,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/gift-card-values/form/:id',
    Component: withFeature(
      FeatureFlagKey.MODULE_GIFT_CARDS,
      GiftCardValueSettingForm,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/reseller-setting',
    Component: withFeature(
      FeatureFlagKey.MODULE_RESELLERS,
      ResellerSettingPage,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/reseller-setting/form/',
    Component: withFeature(
      FeatureFlagKey.MODULE_RESELLERS,
      ResellerForm,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/reseller-setting/form/:id',
    Component: withFeature(
      FeatureFlagKey.MODULE_RESELLERS,
      ResellerForm,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/group-type-setting',
    Component: withFeature(
      FeatureFlagKey.MODULE_GROUP_TYPES,
      GroupTypeSettingPage,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/group-type-setting/form/',
    Component: withFeature(
      FeatureFlagKey.MODULE_GROUP_TYPES,
      GroupTypeForm,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/group-type-setting/form/:id',
    Component: withFeature(
      FeatureFlagKey.MODULE_GROUP_TYPES,
      GroupTypeForm,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/age-groups',
    Component: AgeGroupPresetPage,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/age-groups/form/',
    Component: AgeGroupPresetForm,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/age-groups/form/:id',
    Component: AgeGroupPresetForm,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/group-sizes',
    Component: GroupSizePresetPage,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/group-sizes/form/',
    Component: GroupSizePresetForm,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/group-sizes/form/:id',
    Component: GroupSizePresetForm,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/setting/company/promo-banner',
    Component: withFeature(
      FeatureFlagKey.MODULE_PROMO_BANNER,
      PromoBannerPage,
      '/setting/company/general'
    ),
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/google-calendar',
    Component: GoogleCalendarPage,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/google-calendar/form',
    Component: GoogleCalendarForm,
    loader: combineLoaders(permissionsLoader),
  },
  {
    path: '/google-calendar/form/:email',
    Component: GoogleCalendarForm,
    loader: combineLoaders(permissionsLoader),
  },
  {
    // Target redirect dari BE setelah OAuth callback (jalan di dalem popup).
    // Sengaja tanpa permissionsLoader: cuma nutup popup & kabarin opener.
    path: '/google/callback',
    Component: GoogleOAuthCallback,
  },
];
